import { AcubeJobStatus } from '@repo/database/browser';
import type { FetchAcubeJobsParams } from '@/api/acube-jobs';
import {
  type ApiParamReaders,
  pickApiParams,
  SEARCH_AND_SORT_PARAMS,
  valueParam,
} from '@/lib/helpers/list-api-params';
import type { TranslatableDomainTab } from '@/lib/helpers/list-tabs';
import type { AcubeJobsSearch } from '@/routes/-shared/acubeJobsSearchSchema';

export const ACUBE_JOBS_DEFAULT_TAB = 'all';

export const ACUBE_JOBS_ERROR_TAB = 'error';

/**
 * Tab sullo stato del job (i link vecchi con `?status=` li traduce lo
 * schema). Solo «Errori» ha il contatore, rosso: è il numero da guardare.
 */
export const ACUBE_JOBS_TABS: readonly TranslatableDomainTab<AcubeJobStatus>[] =
  [
    { value: 'all', text: 'acubeJobs.tabs.all' },
    {
      value: ACUBE_JOBS_ERROR_TAB,
      text: 'acubeJobs.tabs.error',
      filter: AcubeJobStatus.ERROR,
      countTone: 'danger',
    },
    {
      value: 'completed',
      text: 'acubeJobs.tabs.completed',
      filter: AcubeJobStatus.COMPLETED,
    },
  ];

type AcubeJobsApiParams = Omit<FetchAcubeJobsParams, 'skip' | 'take'>;

/**
 * Campi di `GET backoffice/acube-jobs`; lo stato arriva dal tab, il tipo lo
 * valida lo schema della rotta.
 */
const ACUBE_JOBS_API_PARAMS: ApiParamReaders<
  AcubeJobsSearch,
  AcubeJobsApiParams
> = {
  ...SEARCH_AND_SORT_PARAMS,
  jobType: valueParam,
  tenantId: valueParam,
};

export function buildAcubeJobsApiParams(
  search: AcubeJobsSearch,
  status: AcubeJobStatus | undefined
): AcubeJobsApiParams {
  return { ...pickApiParams(search, ACUBE_JOBS_API_PARAMS), status };
}

interface AcubeJobsEmptyStateOptions {
  /** Ricerca o filtri attivi. */
  isFiltered: boolean;
  /** Stato del tab attivo (assente su «Tutti»). */
  status: AcubeJobStatus | undefined;
}

/**
 * Titolo dello stato vuoto: con ricerca o filtri è «nessun risultato»; sul
 * tab «Errori» senza filtri è una buona notizia; altrimenti non ci sono job.
 */
export function getAcubeJobsEmptyTitleKey({
  isFiltered,
  status,
}: AcubeJobsEmptyStateOptions): string {
  if (isFiltered) return 'acubeJobs.empty.no_results';
  if (status === AcubeJobStatus.ERROR) return 'acubeJobs.empty.tabs.errors';
  return 'acubeJobs.empty.title';
}
